//Imports
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useImportNotifications } from 'Core/hooks/useImportNotifications';

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
    Pill
} from "@gpstrackit/gtc-react-controls";
import { TagsGroup } from 'Components/Tag';
import { Button } from 'Components/uiControls';
import { HasPermission } from "Core/security";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";
import { Grid } from "@mui/material";
import { getTagColor, completeColumns } from 'Core/data/Helpers';
import ImportResultsModal from "../../../../core/views/importResultsModal";


//Actions
import { sendGenericExport, savePreference, clearRedux, openModalDeleteAsset, openImportAssetsModal } from "Redux/actions";
import { manageSearchAsset, reset, manageSearchEntityList } from './redux/slice';
import { openModalCreateEditAsset } from './modals/createEditAsset/redux/slice';
import { setImportNotificationAssets } from "./modals/importAssets/redux/slice";
//Views
import AdvancedFilters from './modals/advancedFilters';
import CreateEditAsset from './modals/createEditAsset';
import DeleteAsset from './modals/deleteAsset';
import ModalImportAssets from "./modals/importAssets";

const KEY_PREFERENCE = "MAAsset-list";
const INITIAL_STATE = {
    assetType: null,
    assetSubtype: null,
    tags: []
}

const Asset = (props) => {

    const dispatch = useDispatch();

    //Table refs
    const assetTableRef = useRef();

    //Language
    const { messages, locale } = useIntl();

    const searchFields = useMemo(() => ["label", "licensePlate", "vin", "assetSubtype_name", "assetType_label"], []);
    const termsFields = useMemo(() => [{
        fields: "unitId",
        terms: ['NULL'],
        exact: true
    }], []);
    // Permissions
    const user = useSelector((state) => state.securityRedux.user);
    const editablePermissions = HasPermission(user, "", [347])

    // Import notifications hook
    useImportNotifications(
        state => state.assetsImport,
        setImportNotificationAssets
    );

    const paginationAsset = useTablePagination({
        dispatch,
        action: manageSearchAsset,
        searchFields,
        tableRef: assetTableRef,
        sendGenericExport,
        exportName: "assets",
        termsFields
    });


    //Local States
    const [loadPreference, setLoadPreferences] = useState(false);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [selectedItems, setSelectedItems] = useState([]);
    const [preferences, setPreferences] = useState({});
    const [loadColumns, setLoadColumns] = useState(false);
    //Redux States
    const { loading, items, total, entities } = useSelector((state) => state.manageAssetTabRedux);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const openModalCreateEdit = useSelector((state) => state.manageAssetCreateEditRedux.open);
    const openModalDelete = useSelector((state) => state.manageAssetDeleteRedux.open);
    const updateAssetSubtype = useSelector(state => state.manageCreateEditAssetSubtypeRedux.update);
    const openImportModal = useSelector((state) => state.assetsImport.openImport);
    const importState = useSelector((state) => state.assetsImport);

    const menuOptions = useMemo(() => [messages['edit'], messages['delete']], [messages]);

    const currentColumns = useMemo(() => {
        const columns = [
            {
                key: "id",
                title: messages['manage_asset_id'],
                functionString: 'return row?.id',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
            key: "label",
            title: messages['manage_asset_label'],
            functionString: 'return row?.label',
            show: true,
            export: true,
            sort: true,
            width: 200,
            exportValue: (row) => row?.label,
            render: (row) => {
                return (
                        <Grid container alignItems="center">
                            <Grid item xs={10} style={{ wordWrap: 'break-word' }}>
                                <p>{row?.label}</p>
                            </Grid>
                            <Grid item xs={2}>
                                {
                                    editablePermissions && row.companyId !== -1 &&
                                    <MenuComponent
                                        placement="right-start"
                                        options={[messages['edit'], messages['delete']]}
                                        icon="more_vert"
                                        className="showInHover"
                                        onClick={(value) => {
                                            if (value === messages['edit']) {
                                                dispatch(openModalCreateEditAsset({ assetId: row.id }));
                                            } else if (value === messages['delete']) {
                                                dispatch(openModalDeleteAsset({ asset: row }))
                                            }
                                        }}
                                        value={row}
                                    />
                                }
                            </Grid>
                        </Grid>
                    )
                },
            },
            {
                key: "assetType_label",
                title: messages['manage_asset_assetType'],
                functionString: 'return row?.assetType?.label',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.assetType?.label,
                render: (row) => {
                    return (
                        <Grid container alignItems="center">
                            <Grid item xs={10}>
                                <p>{row?.assetType?.label}</p>
                            </Grid>
                        </Grid>
                    )
                },
            },
            {
                key: "assetSubtype_name",
                title: messages['manage_asset_assetSubType'],
                functionString: 'return row?.assetSubtype?.name',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.assetSubtype?.name,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            <p>{row?.assetSubtype?.name}</p>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "externalId",
                title: messages['manage_asset_externalID'],
                functionString: 'return row?.externalId',
                show: true,
                export: true,
                width: 200,
                exportValue: (row) => row?.externalId,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            <p>{row?.externalId}</p>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "vin",
                title: messages['manage_asset_vin'],
                functionString: 'return row?.vin',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.vin,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            <p>{row?.vin}</p>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "inspectionDeviceType",
                title: messages['manage_asset_inspectionDeviceType'],
                functionString: 'return row?.inspectionDeviceType',
                show: true,
                export: true,
                width: 250,
                exportValue: (row) => row?.inspectionDeviceType,
                render: (row) => {
                    let inspectionDeviceType = entities?.find((entity) => entity?.entityId === row?.inspectionDeviceType);
                    return (
                        <Grid container alignItems="center">
                            <Grid item xs={10}>
                                <p>{inspectionDeviceType?.label || row?.inspectionDeviceType}</p>
                            </Grid>
                        </Grid>
                    )
                },
            },
            {
                key: "isActive",
                title: messages['manage_asset_status'],
                functionString: 'return row?.isActive ? "Active" : "Inactive"',
                show: true,
                export: true,
                width: 250,
                exportValue: (row) => row?.isActive ? messages['manage_asset_active'] : messages['manage_asset_inactive'],
                render: (row) => {
                    return (
                        <Grid container alignItems="center">
                            <Pill
                                size="dinamic"
                                item={{
                                    id: row?.id,
                                    label: row?.isActive ? messages['manage_asset_active'] : messages['manage_asset_inactive'],
                                    color: row?.isActive ? '#66B031' : '#DEDEDE'
                                }}
                            />
                        </Grid>
                    )
                },
            },
            {
                key: "tags",
                title: messages['manage_asset_tags'],
                functionString: `
                    return row?.tags?.filter(tag => row?.tagIds?.includes(tag.id)).map(item => item?.label).join("/");
                `,
                show: true,
                export: true,
                width: 150,
                exportValue: (row) => {
                    return row.tags.filter(tag => row.tagIds.includes(tag.id)).map(x => x.label).join('/\r\n');
                },
                render: (row) => {
                    let unitTags = [];
                    if (row?.tagIds?.includes("-99") || row?.tagIds?.includes(-99)) {
                        unitTags = [{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: '#005bf8' }];
                    } else if (row?.tags && row?.tags?.length) {
                        unitTags = row?.tags?.filter((item) => row?.tagIds.includes(item?.id)).map(t => { return { ...t, color: getTagColor(t?.color) } });
                    }

                    return (
                        <TagsGroup
                            options={unitTags}
                            nameField="label"
                            colorField="color"
                        />
                    )
                }
            },
            {
                key: "make",
                title: messages['manage_asset_make'],
                functionString: 'return row?.make',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "model",
                title: messages['manage_asset_model'],
                functionString: 'return row?.model',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "year",
                title: messages['manage_asset_year'],
                functionString: 'return row?.year',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "licensePlate",
                title: messages['manage_asset_license_plate'],
                functionString: 'return row?.licensePlate',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "maintenanceCompany",
                title: messages['manage_asset_maintenanceCompany'],
                functionString: 'return row?.maintenanceCompany',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "registrationCountry",
                title: messages['manage_asset_registrationCountry'],
                functionString: 'return row?.registrationCountry',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "registrationState",
                title: messages['manage_asset_registrationState'],
                functionString: 'return row?.registrationState',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "registrationExpiration",
                title: messages['manage_asset_registrationExpiration'],
                functionString: `
                    const registrationExpiration = row?.registrationExpiration;
                    if (!registrationExpiration) return '';
                    
                    const date = new Date(registrationExpiration);
                    if (isNaN(date.getTime())) return '';
                    
                    const month = date.getMonth() + 1;
                    const day = date.getDate();
                    const year = date.getFullYear();
                    
                    return month + '/' + day + '/' + year;
                `,
                show: false,
                export: true,
                exportOnly: true,
                exportValue: (row) => {
                    const registrationExpiration = row?.registrationExpiration;
                    if (!registrationExpiration) return '';
                    
                    // Convert ISO date to MM/DD/YYYY format
                    const date = new Date(registrationExpiration);
                    if (isNaN(date.getTime())) return ''; // Invalid date check
                    
                    const month = date.getMonth() + 1; // getMonth() returns 0-11
                    const day = date.getDate();
                    const year = date.getFullYear();
                    
                    return `${month}/${day}/${year}`;
                },
            },
            {
                key: "engineSerialNumber",
                title: messages['manage_asset_engineSerialNumber'],
                functionString: 'return row?.engineSerialNumber',
                show: false,
                export: true,
                exportOnly: true,
            },
            {
                key: "chassisSerialNumber",
                title: messages['manage_asset_chassisSerialNumber'],
                functionString: 'return row?.chassisSerialNumber',
                show: false,
                export: true,
                exportOnly: true,
            }

        ];
            if (HasPermission(user, 'evir', [295])) {
                columns.push(
                    {
                    key: 'inspectionTagId',
                    sort: true,
                    show: false, // Hidden from UI
                    title: messages['manage_asset_inspectionTagId'],
                    width: 100,
                    export: true,
                    exportOnly: true,
                    //no render to avoid showing ID in UI but it will exist in export.
                    functionString: 'return row?.inspectionTagId',
                    }
                );
            }
            if (HasPermission(user, 'zlogs', [294])) {
                columns.push({
                key: 'powerUnitNumber',
                sort: true,
                show: false, // Hidden from UI
                title: messages['manage_asset_powerUnitNumber'],
                width: 100,
                export: true,
                exportOnly: true,
                //no render to avoid showing ID in UI but it will exist in export.
                functionString: 'return row?.powerUnitNumber',
                });
            }
    return columns;

}, [messages, entities, dispatch, user]);

    useEffect(() => {
        paginationAsset.setColumns(currentColumns);
        setSelectedItems([]);
        assetTableRef?.current?.setPage(1);
        assetTableRef?.current?.setSelectedItems([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentColumns]);

    useEffect(() => {
        if (!exportResult || exportName !== "assets") return;
        NotificationManager.info(
            messages["exportInProcessBody"],
            messages["exportInProcessTitle"],
            4000,
            null,
            null,
            "info",
            null
        );
        dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }, [exportResult, exportName]);

    useEffect(() => {
        dispatch(manageSearchEntityList({}))

        return () => dispatch(reset());
    }, [])

useEffect(() => {
        if (preferences?.columns?.length && !loadColumns) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));

            // Reorder columns based on preferences (keeping `show`)
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    if (!col) return null;
                    // Always hide exportOnly columns in UI
                    if (col.exportOnly) return { ...col, show: false };
                    return { ...col, show: pref.show };
                })
                .filter(Boolean);

            // Add new columns (not listed in preferences), with `show: true` by default unless exportOnly
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: col.exportOnly ? false : true }));

            const finalColumns = [...reorderedColumns, ...newColumns];

            paginationAsset.setColumns(finalColumns);
            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences.columns, loadColumns, currentColumns]);

    useEffect(() => {
        if (paginationAsset.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationAsset.columns })
            setLoadColumns(false);
        }
    }, [locale, entities]);

    useEffect(() => {
        if (updateAssetSubtype) {
            refreshTable()
        }
    }, [updateAssetSubtype])

    const savePreferences = (preferencesArray) => {
        if (!Array.isArray(preferencesArray)) {
            console.error("Expected an array of preferences");
            return;
        }

        let updatedPreferences = { ...preferences } || {};

        preferencesArray.forEach(({ key, value }) => {
            updatedPreferences[key] = value;
        });

        setPreferences(updatedPreferences);

        dispatch(savePreference({
            entityName: "User",
            entityId: authenticatedId,
            preferenceKey: KEY_PREFERENCE,
            preferenceValue: updatedPreferences
        }));
    };

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
        assetTableRef?.current?.setSelectedItems([]);
    }, []);

    const applyChanges = (field, value) => {
        const updatedParams = {
            [field]: value,
            _conditions: buildConditions(formData),
        };

        clearSelection();

        if (field === '_pageSize') {
            updatedParams._page = 1;
        }
        if (field === '_searchText') {
            setOpenSearch(value);
            if (window.typingTime) {
                clearTimeout(window.typingTime);
            }
            window.typingTime = setTimeout(() => {
                paginationAsset.refresh(updatedParams);
            }, 1000);
        } else {
            paginationAsset.refresh(updatedParams);
        }
    }

    const hasFilters = useCallback(() => {
        const defaultValues = {
            assetType: null,
            assetSubtype: null,
            tags: []
        };

        return Object.keys(formData).some(key => {
            if (Array.isArray(formData[key]) && Array.isArray(defaultValues[key])) {
                return formData[key].length !== defaultValues[key].length ||
                    formData[key].some((item, i) => item !== defaultValues[key][i]);
            }
            return formData[key] !== defaultValues[key];
        });
    }, [formData]);

    const refreshTable = () => {
        clearSelection();
        assetTableRef?.current?.setPage(1);
        applyChanges('_page', 1)
    };

    useEffect(() => {
        if (importState?.importNotification?.status === "SUCCESS" ||
            importState?.importNotification?.status === "PARTIAL_SUCCESS") {
            refreshTable();
        }
    }, [importState?.importNotification, refreshTable]);

    const tableButtons = useMemo(() => ["export", "orderColumn"], []);

    return (
        <div >
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            {editablePermissions &&
                                <IconButton
                                key="add-asset-button"
                                    tooltip={messages["add"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="add_box"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => {
                                        dispatch(openModalCreateEditAsset({}));
                                    }}
                                />
                            }
                            {
                                editablePermissions && (selectedItems?.length && selectedItems.some((item) => item?.companyId !== -1)) ?
                                    <IconButton
                                        key="delete-asset-button"
                                        tooltip={messages["delete"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="delete"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openModalDeleteAsset({ assetList: selectedItems }))
                                        }}
                                        style={{ opacity: !selectedItems.length && "0.6" }}
                                        disabled={!selectedItems.length}
                                    /> : null
                            }
                            <TableButtons
                                key="table-buttons"
                                messages={messages}
                                {...paginationAsset}
                                columns={paginationAsset.columns.filter(col => col.exportOnly !== true)}
                                tableRef={assetTableRef}
                                buttons={tableButtons}
                                onExport={(type) => {
                                    paginationAsset.onExport(type);
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences([{ key: "columns", value: columns }])
                                    paginationAsset.setColumns(columns)
                                }}
                            />
                            { editablePermissions && <Button
                                key="import-assets-button"
                                type="icon"
                                icon="ios_share"
                                size="small"
                                tooltip={messages['fleetView_importUnits']}
                                // placement="bottom"
                                onClick={() => {
                                    dispatch(openImportAssetsModal(true));
                                }}
                                disabled={false}
                            /> }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["manage_asset_search_table"]}
                        searchText={paginationAsset.searchText}
                        onSearchText={paginationAsset.onSearchChange}
                    />
                </Grid>
            </Grid>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        let updatedFormData = {
                            ...formData, ...['assetType', 'assetSubtype', 'tags'].reduce((acc, key) => {
                                if (data?.[key]) acc[key] = data[key];
                                return acc;
                            }, {})
                        };
                        const currentConditions = buildConditions(updatedFormData);

                        paginationAsset.refresh({ _conditions: currentConditions });
                        setFormData(updatedFormData);
                        setPreferences(data);
                        setLoadPreferences(true);
                    }
                }}
            >
                <TableView
                    keyColumn="id"
                    loading={loading}
                    topheight={235}
                    data={items}
                    total={total}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    {...paginationAsset}
                    hideAllInPagination
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationAsset?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences([{ key: "columns", value: finalsColumns }])
                        paginationAsset.setColumns(finalsColumns)
                    }}
                    onChangeSort={(value) => applyChanges('_sort', value)}
                    onChangeRowsPerPage={(value) => applyChanges('_pageSize', value)}
                    onChangePage={(value) => applyChanges('_page', value)}
                />
                <AdvancedFilters
                    tableRef={paginationAsset}
                    formData={formData}
                    setFormData={setFormData}
                    initialState={INITIAL_STATE}
                    applyChanges={applyChanges}
                    clearSelection={clearSelection}
                    hasFilters={hasFilters}
                    savePreferences={savePreferences}
                />
            </Preferences>
            <ImportResultsModal/>
            
            {openModalCreateEdit && <CreateEditAsset refresh={refreshTable} />}
            {openModalDelete && <DeleteAsset refresh={refreshTable} />}
            {openImportModal && <ModalImportAssets />}
        </div>
    )

}

const buildConditions = (formData) => {
    const conditions = [];
    if (formData?.assetType?.entityId) conditions.push({ fields: "assetTypeId", terms: [formData?.assetType?.entityId], exact: true });
    if (formData?.assetSubtype?.id) conditions.push({ fields: "assetSubtypeId", terms: [formData?.assetSubtype?.id], exact: true });
    if (formData?.tags?.length) {
        const tagIds = formData?.tags?.map(tag => tag.id);
        conditions.push({ fields: "tagIds", terms: tagIds, exact: true });
    }
    return conditions;
};

export default Asset;
